import { useCallback, useContext, useEffect, useState, lazy, Suspense } from 'react'
import { UIView, useRouter, useCurrentStateAndParams } from '@uirouter/react'
import AuthContext from '../../authService/Context'
import LoadingUser from './LoadingUser'
import { Alert, Button, Space } from 'antd'
import authService from '../../authService'
import { getFirstLoginAccess } from '../../authService/firstLoginAccess'
import FirstLoginSessionNotice from './FirstLoginSessionNotice'
import { isPasswordRecoveryState } from '../../authService/passwordRecoveryLink'
import { canOpenNavigationEntry, firstNavigationRoute, getNavigationEntries, navigationCodeForState } from '../../states/navigation'
import { isCollaboratorInvitationState } from '../../authService/collaboratorInvitationLink'

const StatusNotActive = lazy(() => import('./StatusNotActive'))
const LoginPage = lazy(() => import('./LoginPage'))
const ForgotPasswordPage = lazy(() => import('./ForgotPasswordPage'))
const RegisterPage = lazy(() => import('./RegisterPage'))
const Layout = lazy(() => import('../../layout'))
const FirstLogin = lazy(() => import('../FirstLogin'))
const CollaboratorInvitation = lazy(() => import('../CollaboratorInvitation'))

export default function Init() {
  const { user, getting, profileError, claimRecovery, firebaseReady, navigation } = useContext(AuthContext)
  const router = useRouter()
  const { state } = useCurrentStateAndParams()
  const { isAuthenticated, status } = user
  const currentStateName = state?.name
  const navigationCode = navigationCodeForState(currentStateName)
  const navigationData = navigation?.data
  const navigationReady = navigation?.status === 'ready'
  const { stateService } = router
  const access = getFirstLoginAccess(user)
  const [wizardTenant, setWizardTenant] = useState(null)
  const onAlreadyCompleted = useCallback(() => setWizardTenant(null), [])
  const recoveryBlocked = ['refreshing', 'reauthenticate', 'failed'].includes(claimRecovery?.status)
  const showWizard = !recoveryBlocked && access.canConfigure && access.tokenReady && status === 'ACTIVE' && (access.needsWizard || wizardTenant === access.tenantId)

  useEffect(() => {
    if (access.needsWizard) setWizardTenant(access.tenantId)
    else if (!isAuthenticated || !access.canConfigure || wizardTenant !== access.tenantId) setWizardTenant(null)
  }, [access.needsWizard, access.tenantId, access.canConfigure, isAuthenticated, wizardTenant])

  useEffect(() => {
    if (isPasswordRecoveryState(currentStateName) || isCollaboratorInvitationState(currentStateName)) return
    if (!isAuthenticated) {
      if (navigationCode && firebaseReady === false) return
      if (!currentStateName || currentStateName === 'firstLogin' || currentStateName === 'App' || currentStateName.startsWith('App.')) {
        stateService.go('App')
      }

      return
    }

    if (getting || !user.profileLoaded || profileError || (status && status !== 'ACTIVE') || access.blockedReason || recoveryBlocked) return
    if (showWizard) {
      if (currentStateName !== 'firstLogin') stateService.go('firstLogin', {}, { location: 'replace' })
    } else if (navigationReady && ['App', 'forgotPassword', 'register', 'firstLogin'].includes(currentStateName)) {
      stateService.go(firstNavigationRoute(navigationData), {}, { location: 'replace' })
    }
  }, [currentStateName, isAuthenticated, stateService, getting, user.profileLoaded, profileError, status, showWizard, access.blockedReason, recoveryBlocked, firebaseReady, navigationCode, navigationReady, navigationData])

  if (isPasswordRecoveryState(currentStateName)) {
    return <Suspense fallback={<LoadingUser />}><ForgotPasswordPage /></Suspense>
  }

  if (isCollaboratorInvitationState(currentStateName)) {
    return <Suspense fallback={<LoadingUser />}><CollaboratorInvitation /></Suspense>
  }

  if (isAuthenticated && (recoveryBlocked || access.blockedReason)) {
    return <FirstLoginSessionNotice reason={access.blockedReason} recovery={claimRecovery} />
  }

  if (getting || (navigationCode && firebaseReady === false)) return <LoadingUser />

  if (isAuthenticated && profileError) {
    return <div style={{ maxWidth: 600, margin: '15vh auto', padding: 24 }}>
      <Alert type="warning" showIcon message={profileError} />
      <Space style={{ marginTop: 20 }}><Button onClick={() => authService.retryProfile()}>Reintentar</Button><Button onClick={() => authService.logout()}>Volver al login</Button></Space>
    </div>
  }

  if (isAuthenticated && !user.profileLoaded) return <LoadingUser />

  if (isAuthenticated && status && status !== 'ACTIVE'){
    return (
      <Suspense fallback={<LoadingUser/>}>
        <StatusNotActive/>
      </Suspense>
    )
  }

  if (isAuthenticated && showWizard) {
    return <Suspense fallback={<LoadingUser />}><FirstLogin key={access.tenantId} tenantId={access.tenantId} onAlreadyCompleted={onAlreadyCompleted} /></Suspense>
  }

  let unauthenticatedView = <LoginPage />

  if (currentStateName === 'register') {
    unauthenticatedView = <RegisterPage />
  }

  let content = <UIView name="main" />
  if (isAuthenticated && (navigationCode || ['App', 'firstLogin', 'forgotPassword', 'register'].includes(currentStateName))) {
    if (navigation?.status === 'error') {
      content = <Alert type="warning" showIcon message={navigation.message} action={navigation.httpStatus === 401
        ? <Button onClick={() => authService.logout()}>Volver al login</Button>
        : navigation.httpStatus !== 403 && <Button onClick={navigation.retry}>Reintentar</Button>} />
    } else if (!navigationReady) {
      content = <LoadingUser />
    } else if (navigationCode && !canOpenNavigationEntry(getNavigationEntries(navigationData).get(navigationCode))) {
      content = <Alert type="warning" showIcon message="Sin acceso a esta opción" action={
        <Button onClick={() => stateService.go(firstNavigationRoute(navigationData))}>Volver a la navegación</Button>
      } />
    }
  }

  return (
    <Suspense fallback={<LoadingUser />}>
      {isAuthenticated ? (
        <Layout>
          {content}
        </Layout>
      ) : (
        unauthenticatedView
      )}
    </Suspense>
  )
}
